"use client";

import Link from "next/link";
import { useState } from "react";
import type { SubmissionPayload } from "@/lib/submit/payload";
import { normalizeUrl } from "@/lib/submit/normalizeUrl";
import type { RawInput } from "@/lib/submit/rawInput";
import type { EvidenceGps } from "@/lib/submit/evidenceGps";
import {
  block,
  getFingerprint,
  postSubmission,
  prepareEvidenceFile,
  progressText,
  type FailureContext,
  type SubmitProgress,
} from "@/lib/submit/clientSubmit";
import { useCaptcha } from "./turnstile";

const inputCls =
  "w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-text-primary placeholder:text-text-muted focus:border-accent-cool focus:outline-none";

/**
 * Unified contribution box (operator decision 2026-06). Adapts copy based on
 * whether the venue already has happy-hour data. Always posts a `targetType:
 * "intent"` submission — the backend routes it through the interpreter which
 * fans out concrete edits for admin review. Nothing goes live until an operator
 * approves.
 */
export function Contribute({
  venueId,
  venueName,
  hasHappyHour,
}: {
  venueId: string;
  venueName: string;
  hasHappyHour: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [note, setNote] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [imageGps, setImageGps] = useState<EvidenceGps | null>(null);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const captcha = useCaptcha();
  const [progress, setProgress] = useState<SubmitProgress | null>(null);
  const [state, setState] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [statusUrl, setStatusUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function ctx(extra: RawInput = {}): FailureContext {
    const raw: RawInput = { ...extra };
    if (sourceUrl.trim()) raw.sourceUrl = sourceUrl;
    if (note.trim()) raw.note = note;
    if (imageName) raw.attachedFile = imageName;
    return { form: "contribute", targetType: "intent", targetId: venueId, rawInput: raw };
  }

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const prepared = await prepareEvidenceFile(file);
    if (!prepared.ok) {
      setError(
        block(
          ctx({ attachedFile: file.name, attachedFileType: file.type || "(none)", attachedFileBytes: String(file.size) }),
          prepared.reason,
          prepared.error,
        ),
      );
      return;
    }
    setImageDataUrl(prepared.dataUrl);
    setImageName(prepared.name);
    setImageGps(prepared.gps);
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const c = ctx();

    // Only block when there's literally nothing to send. A link we can't parse, or a
    // note with no source, still goes through — it lands in the review queue flagged,
    // with exactly what the visitor typed.
    const trimmedNote = note.trim();
    if (!sourceUrl.trim() && !imageDataUrl && !trimmedNote) {
      setError(
        block(c, "empty_contribution", "Add a photo of the menu, a link, or a quick note about the happy hour."),
      );
      return;
    }

    setState("submitting");
    setProgress(null);
    const payload: SubmissionPayload = {
      targetType: "intent",
      targetId: venueId,
      diff: {
        before: null,
        after: trimmedNote ? { note: trimmedNote } : {},
        sourceUrl: sourceUrl.trim() || null,
        summary: (trimmedNote || `Contribution for ${venueName}`).slice(0, 120),
      },
      fingerprint: getFingerprint(),
      email: email.trim() || null,
      captchaToken: await captcha.getToken(),
      evidenceImage: imageDataUrl,
      evidenceGps: imageGps,
      website,
    };

    const result = await postSubmission(payload, c, setProgress);
    if (!result.ok) {
      setError(result.error);
      setState("error");
      captcha.reset();
      return;
    }
    setStatusUrl(result.statusUrl);
    setState("done");
  }

  if (state === "done") {
    return (
      <div className="rounded-lg border border-border bg-bg-surface p-4 text-sm">
        <p className="text-text-primary">
          🙏 Thanks for being a friend.
        </p>
        <p className="mt-1 text-text-muted">Your update is in the review queue.</p>
        {statusUrl && (
          <Link
            href={statusUrl}
            className="mt-2 inline-block text-accent-cool hover:underline"
          >
            Check the status of your submission →
          </Link>
        )}
      </div>
    );
  }

  const triggerLabel = hasHappyHour ? "Suggest a change" : "Add a happy hour";
  const heading = hasHappyHour
    ? "Something off? Tell us"
    : `Know ${venueName}'s happy hour? Add it`;
  // The has-HH page section already explains the flow right above the trigger —
  // repeating it inside the form read as noise (operator feedback 2026-06-11).
  const blurb = hasHappyHour
    ? null
    : "A human reviews everything before it goes live.";

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={state === "submitting"}
        className="rounded-md bg-accent-warm px-4 py-2 text-sm font-medium text-bg-deep transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {open ? "Cancel" : triggerLabel}
      </button>

      {open && (
        <form
          onSubmit={onSubmit}
          className="mt-3 space-y-4 rounded-lg border border-border bg-bg-surface p-4 text-sm"
        >
        <fieldset disabled={state === "submitting"} className="contents">
          <div>
            <p className="mb-1 font-medium text-text-primary">{heading}</p>
            {blurb && <p className="text-xs text-text-muted">{blurb}</p>}
          </div>

          <div className="rounded-md border border-border bg-bg-elevated/60 p-3">
            <label className="mb-2 block text-text-muted">
              Snap a pic of the HH menu, or paste a link{" "}
              <span className="text-text-muted/70">(either works — no source? just leave a note below)</span>
            </label>
            {/* Link and photo are PARALLEL options, not steps — either alone is a
                valid source, and both together is better. Mobile-first: photos come
                from phones, so the photo button leads (full-width, centered "or"
                below); sm+ flips to input | or | button on one row. */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="order-1 flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-border bg-bg-elevated px-3 py-2 text-xs text-text-primary hover:border-accent-cool sm:order-3 sm:w-auto">
                {imageDataUrl ? "Change file" : "📷 Add a photo of the menu"}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={onPickImage}
                />
              </label>
              <span className="order-2 text-center text-xs uppercase text-text-muted">
                or
              </span>
              <input
                className={`${inputCls} order-3 sm:order-1 sm:min-w-[12rem] sm:flex-1`}
                type="text"
                inputMode="url"
                autoComplete="off"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                onBlur={(e) => {
                  const n = normalizeUrl(e.target.value);
                  if (n) setSourceUrl(n);
                }}
                placeholder="Paste a link/URL"
              />
            </div>
            <div className="mt-2 flex items-center gap-3 empty:hidden">
              {imageDataUrl && (
                <span className="flex items-center gap-2 text-xs text-text-muted">
                  {imageDataUrl.startsWith("data:application/pdf") ? (
                    <span className="flex h-8 w-8 items-center justify-center rounded bg-bg-elevated text-base">
                      📄
                    </span>
                  ) : (
                    /* eslint-disable-next-line @next/next/no-img-element */
                    <img
                      src={imageDataUrl}
                      alt="Selected menu"
                      className="h-8 w-8 rounded object-cover"
                    />
                  )}
                  <span className="max-w-[10rem] truncate">{imageName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setImageDataUrl(null);
                      setImageName(null);
                      setImageGps(null);
                    }}
                    className="text-accent-hot hover:underline"
                  >
                    remove
                  </button>
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-text-muted">
              Anything else? (optional)
            </label>
            <textarea
              className={inputCls}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Tacos are $3 now, not $2 · Happy hour runs til close now"
            />
          </div>

          <div>
            <label className="mb-1 block text-text-muted">
              Your email (optional)
            </label>
            <input
              className={inputCls}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="If you want us to follow up"
            />
          </div>

          {/* Honeypot — hidden from humans, off the accessibility tree (§5.1.3). */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="hidden"
          />

          {captcha.widget}

          {error && <p className="text-accent-hot">{error}</p>}

          <button
            type="submit"
            disabled={state === "submitting"}
            className="flex items-center gap-2 rounded-md bg-accent-warm px-4 py-2 font-medium text-bg-deep transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {state === "submitting" && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-bg-deep border-t-transparent" />
            )}
            {state === "submitting" ? "Submitting…" : "Submit for review"}
          </button>
          {state === "submitting" && (
            <p role="status" aria-live="polite" className="text-xs text-text-muted">
              {progressText(progress, Boolean(imageDataUrl))}
            </p>
          )}

        </fieldset>
        </form>
      )}
    </div>
  );
}
